<template>
  <v-combobox
    :model-value="modelValue || null"
    :items="items"
    item-title="label"
    item-value="key"
    return-object
    no-filter
    clearable
    variant="outlined"
    :density="look === 'details' ? 'compact' : 'default'"
    :rounded="look === 'details' ? 0 : undefined"
    :placeholder="placeholder"
    :loading="loading"
    :disabled="disabled"
    :error-messages="error ? [error] : []"
    :hint="hint"
    :persistent-hint="Boolean(hint)"
    :hide-details="!error && !hint"
    :menu-props="{ maxHeight: 320, maxWidth: 520 }"
    :class="['alice-job', `alice-job--${look}`]"
    autocomplete="off"
    spellcheck="false"
    @update:model-value="onChange"
    @update:search="onSearch"
    @update:focused="onFocused"
  >
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :title="item.raw.label">
        <template v-if="item.raw.detail" #subtitle>
          {{ item.raw.detail }}
        </template>
      </v-list-item>
    </template>
    <template #no-data>
      <div class="alice-job__empty">{{ emptyText }}</div>
    </template>
  </v-combobox>
</template>

<script>
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import {
  jobNumberError,
  jobRowDetail,
  jobRowLabel,
  normaliseJobNumber,
} from "@/utils/jobNumber";

// The "Jobnummer" field: the client's Productive jobs as suggestions
// (searchProductiveJobs, newest first; typing searches number and name),
// free text allowed. The model value is the text in the field; `pick` is the
// listed job chosen from the menu (null once the text no longer is its
// number), so the caller can send its Productive key along when it saves.
// v-combobox updates the model on every keystroke, so a value only counts as
// a search when it is neither the value the caller set nor the job just
// picked.
export default {
  name: "JobNumberPicker",
  props: {
    modelValue: { type: String, default: "" },
    clientId: { type: String, default: null },
    clientName: { type: String, default: null },
    // "dialog": a Vuetify field like the others in the New project dialog;
    // "details": the hairline inputs of the Details page.
    look: { type: String, default: "dialog" },
    placeholder: { type: String, default: "Jobnummer (optioneel)" },
    hint: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "pick"],
  data() {
    return {
      rows: [],
      loading: false,
      failed: false,
      // The rows were fetched for this client and search.
      fetchedFor: null,
      seq: 0,
      // The number of the row picked from the menu, and the number whose
      // text shows the client's list rather than searching (the caller's
      // value, or the row just picked).
      pickedNumber: null,
      quiet: normaliseJobNumber(this.modelValue),
      // The last value this field emitted, to tell the caller's own changes
      // from echoes.
      emitted: null,
    };
  },
  computed: {
    // item-value is the Productive key, so a typed number is never swapped
    // for a row's label: the field shows what was typed or picked.
    items() {
      return this.rows.map((row) => ({
        key: row.key,
        number: row.number,
        label: jobRowLabel(row),
        detail: jobRowDetail(row),
        row,
      }));
    },
    hasClient() {
      return Boolean(this.clientId || (this.clientName || "").trim());
    },
    error() {
      return jobNumberError(this.modelValue);
    },
    emptyText() {
      if (this.loading) return "Productive doorzoeken…";
      if (this.failed) {
        return "Productive-jobs konden niet worden geladen; typ het jobnummer.";
      }
      return this.hasClient
        ? "Geen job gevonden in Productive; typ het jobnummer."
        : "Kies eerst een klant, of typ het jobnummer.";
    },
  },
  watch: {
    clientId() {
      this.refresh();
    },
    clientName() {
      if (!this.clientId) this.refresh();
    },
    // Set by the caller (a project loaded, a save stored): not a search.
    modelValue(value) {
      if (value === this.emitted) return;
      this.quiet = normaliseJobNumber(value);
      this.pickedNumber = null;
    },
  },
  mounted() {
    this.refresh();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    // The client's newest jobs (no search), or what the text finds.
    refresh(q = "") {
      clearTimeout(this.timer);
      const key = `${this.clientId || ""}|${(
        this.clientName || ""
      ).trim()}|${q}`;
      if (key === this.fetchedFor) return;
      if (!this.hasClient && !/\d{3}/.test(q)) {
        this.rows = [];
        this.fetchedFor = key;
        return;
      }
      this.timer = setTimeout(() => this.fetch(q, key), q ? 250 : 0);
    },
    async fetch(q, key) {
      const seq = ++this.seq;
      this.loading = true;
      this.failed = false;
      try {
        const call = httpsCallable(functions, "searchProductiveJobs", {
          timeout: 30000,
        });
        const res = await call({
          clientId: this.clientId || null,
          clientName: (this.clientName || "").trim() || null,
          q: q || null,
        });
        if (seq !== this.seq) return;
        this.rows = (res.data && res.data.rows) || [];
        this.fetchedFor = key;
      } catch (err) {
        if (seq !== this.seq) return;
        console.warn("searchProductiveJobs failed:", err);
        this.rows = [];
        this.failed = true;
      } finally {
        if (seq === this.seq) this.loading = false;
      }
    },
    emitValue(value) {
      this.emitted = value;
      this.$emit("update:modelValue", value);
    },
    // A row picked from the menu, the field cleared, or text typed.
    onChange(val) {
      if (val && typeof val === "object") {
        this.pickedNumber = val.number;
        this.quiet = val.number;
        this.emitValue(val.number);
        this.$emit("pick", val.row);
        return;
      }
      const textValue = typeof val === "string" ? val : "";
      if (textValue !== (this.modelValue || "")) this.emitValue(textValue);
      if (!textValue.trim()) this.dropPick();
    },
    // Every keystroke. Selecting a row puts its label in the input: that is
    // the row, not text to search for.
    onSearch(value) {
      const textValue = typeof value === "string" ? value : "";
      if (this.items.some((i) => i.label === textValue)) return;
      if (textValue !== (this.modelValue || "")) this.emitValue(textValue);
      const code = normaliseJobNumber(textValue);
      if (this.pickedNumber && code !== this.pickedNumber) this.dropPick();
      // The client's list for an empty field, the caller's value or the job
      // just picked; anything else is a search.
      const q = textValue.trim();
      this.refresh(!q || (code && code === this.quiet) ? "" : q);
    },
    dropPick() {
      if (this.pickedNumber === null) return;
      this.pickedNumber = null;
      this.$emit("pick", null);
    },
    // A list that failed to load is tried again when the field is entered.
    onFocused(focused) {
      if (focused && this.failed && !this.loading) {
        this.fetchedFor = null;
        this.refresh();
      }
    },
  },
};
</script>

<style scoped>
.alice-job__empty {
  padding: 10px 16px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}

/* Details page: the hairline inputs of .alice-det__input. */
.alice-job--details :deep(.v-field) {
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--vp-black);
  background: var(--vp-white);
  --v-field-padding-start: 12px;
  --v-field-padding-end: 8px;
}
.alice-job--details :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  --v-field-border-width: 1px;
  color: var(--border-hairline);
}
.alice-job--details :deep(.v-field--focused .v-field__outline) {
  --v-field-border-width: 1px;
  color: var(--vp-black);
}
.alice-job--details :deep(.v-field--error .v-field__outline) {
  color: var(--vp-rood-100);
}
.alice-job--details :deep(.v-field__input) {
  min-height: 42px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.alice-job--details :deep(.v-field__input input::placeholder) {
  color: var(--fg-3);
  opacity: 1;
}
.alice-job--details :deep(.v-messages) {
  font-family: var(--font-ui);
  font-size: 12px;
}
</style>
